import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface XY {
  x: number;
  y: number;
}

export type QuadPoints = [XY, XY, XY, XY];

interface StepCropProps {
  imageUrl: string;
  onCropComplete: (croppedUrl: string) => void;
  resetSignal: number;
  /** 원근보정 엔진(OpenCV) 준비 여부 변화 알림 — 부모가 "다음" 버튼 게이팅에 사용 */
  onCvReady?: (ready: boolean) => void;
}

const HANDLE_RADIUS = 5;
const HANDLE_HIT_RADIUS = 18;

// Fixed canvas dimensions — same as StepSplit
const CANVAS_W = 1000;
const CANVAS_H = 600;

// 원근보정 엔진(OpenCV)은 외부 CDN(docs.opencv.org) 대신 자체 호스팅한다.
// opencv.org 는 옛 버전을 종종 삭제해(4.10.0/4.11.0 현재 404) 외부 의존이 장애로 직결됨
// → public/opencv/opencv.js (v4.9.0) 로 동봉. 버전 변경 시 이 파일을 교체.
const OPEN_CV_SCRIPT_SRC = "/opencv/opencv.js";

/**
 * OpenCV(약 9MB) 스크립트를 1회 로드한다. 이미 로딩 중이거나 로드된 스크립트를 재사용하며
 * (모달 열 때 미리 호출해 선로딩 가능), cache-busting 을 쓰지 않아 브라우저 캐시를 활용한다.
 */
export function preloadOpenCv() {
  if (typeof window === "undefined") return;
  if (window.cv || document.getElementById("open-cv")) return;
  const script = document.createElement("script");
  script.src = OPEN_CV_SCRIPT_SRC;
  script.id = "open-cv";
  script.async = true;
  script.type = "text/javascript";
  script.onerror = () => console.error("Failed to load OpenCV script.");
  document.body.appendChild(script);
}

/**
 * OpenCV 준비되면 cv 인스턴스를 반환(아니면 null). 로드는 preloadOpenCv 에 위임하고,
 * 스크립트/런타임 초기화 시점이 제각각이라 폴링으로 안전하게 준비를 감지한다.
 */
function useOpenCv() {
  const [cv, setCv] = useState<typeof window.cv | null>(null);

  useEffect(() => {
    if (cv) return;
    let cancelled = false;

    const tryReady = () => {
      if (cancelled) return true;
      if (window.cv && typeof window.cv.imread === "function") {
        setCv(window.cv);
        return true;
      }
      return false;
    };
    if (tryReady()) return;

    preloadOpenCv();
    if (window.cv) {
      window.cv["onRuntimeInitialized"] = tryReady;
    }
    const poll = setInterval(() => {
      if (tryReady()) clearInterval(poll);
    }, 300);

    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [cv]);

  return cv;
}

export function StepCrop({ imageUrl, onCropComplete, resetSignal, onCvReady }: StepCropProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const cv = useOpenCv();

  // 부모가 원근보정 엔진 준비 상태를 알 수 있게 — 2단계 "다음" 버튼 게이팅에 사용
  useEffect(() => {
    onCvReady?.(!!cv);
  }, [cv, onCvReady]);

  const pointsRef = useRef<QuadPoints>([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ]);
  const imgDisplayRef = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const draggingIndexRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;

      // Draw onto hidden cv-input canvas for OpenCV imread
      const cvCanvas = document.getElementById("mc-cv-input") as HTMLCanvasElement | null;
      if (cvCanvas) {
        cvCanvas.width = img.naturalWidth;
        cvCanvas.height = img.naturalHeight;
        const ctx = cvCanvas.getContext("2d");
        ctx?.clearRect(0, 0, cvCanvas.width, cvCanvas.height);
        setTimeout(() => {
          ctx?.drawImage(img, 0, 0);
        }, 0);
      }

      setImgLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const initPoints = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const canvasRatio = CANVAS_W / CANVAS_H;
    let dw: number;
    let dh: number;
    if (imgRatio > canvasRatio) {
      dw = CANVAS_W;
      dh = CANVAS_W / imgRatio;
    } else {
      dh = CANVAS_H;
      dw = CANVAS_H * imgRatio;
    }
    const dx = (CANVAS_W - dw) / 2;
    const dy = (CANVAS_H - dh) / 2;
    imgDisplayRef.current = { x: dx, y: dy, w: dw, h: dh };

    pointsRef.current = [
      { x: dx, y: dy },
      { x: dx + dw, y: dy },
      { x: dx + dw, y: dy + dh },
      { x: dx, y: dy + dh },
    ];
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x: dx, y: dy, w: dw, h: dh } = imgDisplayRef.current;
    const pts = pointsRef.current;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.drawImage(img, dx, dy, dw, dh);

    // Dark overlay with quad cutout
    ctx.save();
    ctx.beginPath();
    ctx.rect(dx, dy, dw, dh);
    ctx.moveTo(pts[0].x, pts[0].y);
    ctx.lineTo(pts[1].x, pts[1].y);
    ctx.lineTo(pts[2].x, pts[2].y);
    ctx.lineTo(pts[3].x, pts[3].y);
    ctx.closePath();
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fill("evenodd");
    ctx.restore();

    // Quad border
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    ctx.lineTo(pts[1].x, pts[1].y);
    ctx.lineTo(pts[2].x, pts[2].y);
    ctx.lineTo(pts[3].x, pts[3].y);
    ctx.closePath();
    ctx.strokeStyle = "red";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Corner handles
    for (const p of pts) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.strokeStyle = "red";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, []);

  // Initial layout + draw
  useEffect(() => {
    if (!imgLoaded) return;
    initPoints();
    draw();
  }, [imgLoaded, initPoints, draw]);

  // Reset
  useEffect(() => {
    if (imgLoaded) {
      initPoints();
      draw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const getCanvasCoords = useCallback((e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const getHitIndex = (mx: number, my: number): number | null => {
    const pts = pointsRef.current;
    for (let i = 0; i < pts.length; i++) {
      const dist = Math.sqrt((mx - pts[i].x) ** 2 + (my - pts[i].y) ** 2);
      if (dist <= HANDLE_HIT_RADIUS) return i;
    }
    return null;
  };

  const clampToImage = (p: XY): XY => {
    const { x: dx, y: dy, w: dw, h: dh } = imgDisplayRef.current;
    return {
      x: Math.max(dx, Math.min(dx + dw, p.x)),
      y: Math.max(dy, Math.min(dy + dh, p.y)),
    };
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const { x, y } = getCanvasCoords(e);
    const idx = getHitIndex(x, y);
    if (idx !== null) {
      draggingIndexRef.current = idx;
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = "grabbing";
      e.preventDefault();
    }
  }, [getCanvasCoords]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggingIndexRef.current === null) {
      const { x, y } = getCanvasCoords(e);
      const canvas = canvasRef.current;
      if (canvas) {
        const hit = getHitIndex(x, y);
        canvas.style.cursor = hit !== null ? "grab" : "default";
      }
      return;
    }

    e.preventDefault();
    const { x, y } = getCanvasCoords(e);
    pointsRef.current[draggingIndexRef.current] = clampToImage({ x, y });

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [getCanvasCoords, draw]);

  const handleMouseUp = useCallback(() => {
    if (draggingIndexRef.current !== null) {
      draggingIndexRef.current = null;
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = "grab";
      draw();
    }
  }, [draw]);

  // Expose crop apply function — synchronous, same pattern as legacy
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__multiCameraCropApply = () => {
      const img = imgRef.current;
      if (!img) return;

      const pts = pointsRef.current;
      const imgD = imgDisplayRef.current;

      // Convert display coords → original image pixel coords
      const srcPts = pts.map((p) => ({
        x: ((p.x - imgD.x) / imgD.w) * img.naturalWidth,
        y: ((p.y - imgD.y) / imgD.h) * img.naturalHeight,
      }));

      // 출력 크기 = 선택한 사각형(quad)의 실제 변 길이 (원본 픽셀 기준).
      // 원본 전체 크기(naturalWidth×naturalHeight)로 강제하면, 세로로 긴 영역을
      // 잘라도 원본의 가로 비율로 늘어나 눌리고 좌우가 잘리는 왜곡이 발생한다.
      // 상/하변·좌/우변 평균 길이로 계산해 선택 영역의 비율을 보존한다.
      const dist = (a: XY, b: XY) => Math.hypot(a.x - b.x, a.y - b.y);
      const outW = Math.max(
        1,
        Math.round((dist(srcPts[0], srcPts[1]) + dist(srcPts[3], srcPts[2])) / 2),
      );
      const outH = Math.max(
        1,
        Math.round((dist(srcPts[0], srcPts[3]) + dist(srcPts[1], srcPts[2])) / 2),
      );

      // If OpenCV is loaded, use perspective warp (same as legacy)
      if (cv) {
        // Points order: our quad is [TL, TR, BR, BL]
        // convertPointsToCvPoints expects [LT, LB, RB, RT] → [LT, RT, LB, RB].flat()
        // Our order [TL, TR, BR, BL] maps to legacy [LT, RT, RB, LB]
        // Legacy: input [LT, LB, RB, RT] → output [LT, RT, LB, RB]
        // We have [TL=0, TR=1, BR=2, BL=3] → need [TL, TR, BL, BR] for OpenCV
        const cvPoints = [
          srcPts[0].x, srcPts[0].y, // TL
          srcPts[1].x, srcPts[1].y, // TR
          srcPts[3].x, srcPts[3].y, // BL
          srcPts[2].x, srcPts[2].y, // BR
        ];

        // 할당한 cv.Mat 은 finally 에서 일괄 해제 (예외로 중단돼도 누수 방지)
        const deletables: { delete: () => void }[] = [];
        try {
          const src = cv.imread("mc-cv-input");
          deletables.push(src);
          const dst = new cv.Mat();
          deletables.push(dst);
          const dsize = new cv.Size(outW, outH);
          const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, cvPoints);
          deletables.push(srcTri);
          // prettier-ignore
          const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, outW, 0, 0, outH, outW, outH]);
          deletables.push(dstTri);
          const M = cv.getPerspectiveTransform(srcTri, dstTri);
          deletables.push(M);
          const srcClone = src.clone();
          deletables.push(srcClone);
          // prettier-ignore
          cv.warpPerspective(srcClone, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());

          const cvOut = document.getElementById("mc-cv-output") as HTMLCanvasElement;
          cvOut.width = outW;
          cvOut.height = outH;

          const rect = new cv.Rect(0, 0, outW, outH);
          const dstR = dst.roi(rect);
          deletables.push(dstR);

          cv.imshow("mc-cv-output", dstR);

          const url = cvOut.toDataURL("image/png");
          onCropComplete(url);
          return;
        } catch (e) {
          // 초대형 이미지 등으로 OpenCV 가 메모리 초과로 던지면(숫자 예외) 앱을 죽이는 대신
          // 아래 bounding-box 폴백으로 진행. 업로드 단계 축소로 대부분 예방되지만 최후 보루.
          console.error("[multi-camera] OpenCV 원근보정 실패, 단순 자르기로 폴백", e);
        } finally {
          for (const m of deletables) {
            try {
              m.delete();
            } catch {
              /* 이미 해제됐거나 무효한 Mat — 무시 */
            }
          }
        }
      }

      // Fallback: bounding-box crop without perspective correction
      const minX = Math.max(0, Math.min(...srcPts.map((p) => p.x)));
      const minY = Math.max(0, Math.min(...srcPts.map((p) => p.y)));
      const maxX = Math.min(outW, Math.max(...srcPts.map((p) => p.x)));
      const maxY = Math.min(outH, Math.max(...srcPts.map((p) => p.y)));

      const cropW = Math.round(maxX - minX);
      const cropH = Math.round(maxY - minY);
      if (cropW <= 0 || cropH <= 0) return;

      const offscreen = document.createElement("canvas");
      offscreen.width = cropW;
      offscreen.height = cropH;
      const ctx = offscreen.getContext("2d")!;
      ctx.drawImage(
        img,
        Math.round(minX), Math.round(minY), cropW, cropH,
        0, 0, cropW, cropH,
      );

      const url = offscreen.toDataURL("image/png");
      onCropComplete(url);
    };

    return () => {
      delete (window as unknown as Record<string, unknown>).__multiCameraCropApply;
    };
  }, [cv, onCropComplete]);

  return (
    <Container>
      <StyledCanvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      <HiddenCanvas id="mc-cv-input" />
      <HiddenCanvas id="mc-cv-output" />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  background: #1b1c1f;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledCanvas = styled.canvas`
  max-width: 100%;
  max-height: 100%;
  display: block;
`;

const HiddenCanvas = styled.canvas`
  position: fixed;
  top: -9999px;
  left: -9999px;
`;
